// What players and your own games did from the position on the board: the two cards under the study's live rows.
// The repertoire and the engine are components/study/LivePanel.tsx since 2026-11-01.

import { store, game } from "../state/index.ts";
import { Chess } from "#vendor/chessjs/chess.js";
import * as config from "./config.js";

// DOM elements
let quickSparringValue = null;
let quickSparringBar = null;
let quickSparringDetail = null;
let quickSparringFooter = null;
let quickMyGamesValue = null;
let quickMyGamesBar = null;
let quickMyGamesTotal = null;
let quickMyGamesDetail = null;
let quickMyGamesFooter = null;

export function initQuickStatus(elements) {
    quickSparringValue = elements.quickSparringValue;
    quickSparringBar = elements.quickSparringBar;
    quickSparringDetail = elements.quickSparringDetail;
    quickSparringFooter = document.getElementById("quickSparringFooter");
    quickMyGamesValue = elements.quickMyGamesValue;
    quickMyGamesBar = elements.quickMyGamesBar;
    quickMyGamesTotal = elements.quickMyGamesTotal;
    quickMyGamesDetail = elements.quickMyGamesDetail;
    quickMyGamesFooter = document.getElementById("quickMyGamesFooter");

    // Subscribe to store changes
    store.subscribe('book.stats', () => {
        renderBookStats();
        renderSparringFooter();
    });
    store.subscribe('book.error', () => renderBookStats());
    store.subscribe('book.latestMoves', () => {
        renderSparringDetail();
        computeMoveDiffs();
    });
    store.subscribe('myGames.latestMoves', () => {
        renderMyGamesStats();
        computeMoveDiffs();
        renderMyGamesDetail();
    });
    store.subscribe('myGames.plans', () => renderMyGamesDetail());
    store.subscribe('myGames.moveDiffs', () => {
        renderMyGamesDetail();
        renderMyGamesFooter();
    });
    store.subscribe('myGames.isLoading', () => renderMyGamesStats());
    store.subscribe('myGames.error', () => {
        renderMyGamesStats();
        renderMyGamesDetail();
    });
    store.subscribe('board.userSide', () => renderMyGamesHeader());

    // Card expansion is driven by dedicated footer buttons, not by clicking the card itself
    renderMyGamesHeader();
    renderSparringFooter();
    renderMyGamesFooter();
}

/**
 * Get the FEN to use for UCI→SAN conversion (stored FEN from when moves were loaded).
 */
function getMyGamesFen() {
    return store.get('myGames.fen') || game.fen();
}

/**
 * Convert a single UCI move to SAN at a given position (does not advance position).
 */
function uciToSan(uci, fen) {
    try {
        const tempGame = new Chess(fen || game.fen());
        const from = uci.slice(0, 2);
        const to = uci.slice(2, 4);
        const promotion = uci.length > 4 ? uci[4] : undefined;
        const result = tempGame.move({ from, to, promotion });
        return result ? result.san : uci;
    } catch {
        return uci;
    }
}

/**
 * Convert a sequence of UCI moves to SAN notation, replaying from a given position.
 */
function uciSequenceToSan(uciMoves, fen) {
    try {
        const tempGame = new Chess(fen || game.fen());
        return uciMoves.map(uci => {
            const from = uci.slice(0, 2);
            const to = uci.slice(2, 4);
            const promotion = uci.length > 4 ? uci[4] : undefined;
            const result = tempGame.move({ from, to, promotion });
            return result ? result.san : uci;
        });
    } catch {
        return uciMoves;
    }
}

/**
 * Compare user's win rate per move vs the sparring database.
 * Stores result in myGames.moveDiffs as [{ move, diff, myWinPct, sparringWinPct }].
 */
function computeMoveDiffs() {
    const myMoves = store.get('myGames.latestMoves') || [];
    const sparringMoves = store.get('book.latestMoves') || [];

    if (!myMoves.length || !sparringMoves.length) {
        store.set('myGames.moveDiffs', []);
        return;
    }

    // Determine user's color to pick the right sparring win rate
    const userSide = config.getSelectedBoardSide()[0]; // 'w' or 'b'
    const isWhite = userSide === 'w';

    // Build sparring win rate map by UCI
    const sparringByUci = {};
    for (const m of sparringMoves) {
        if (m.uci) {
            const total = (m.white || 0) + (m.draws || 0) + (m.black || 0);
            sparringByUci[m.uci] = total > 0
                ? Math.round(((isWhite ? m.white : m.black) / total) * 100)
                : 0;
        }
    }

    const diffs = myMoves.map(m => {
        const myWinPct = m.total > 0 ? Math.round((m.wins / m.total) * 100) : 0;
        const sparringWinPct = sparringByUci[m.move];
        if (sparringWinPct === undefined) {
            return { move: m.move, diff: null, myWinPct, sparringWinPct: null };
        }
        return {
            move: m.move,
            diff: myWinPct - sparringWinPct,
            myWinPct,
            sparringWinPct,
        };
    });

    store.set('myGames.moveDiffs', diffs);
}

// Helper to format large numbers
function formatNumber(num) {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
    if (num >= 1000) return (num / 1000).toFixed(1) + "K";
    return num.toString();
}

function renderBookStats() {
    if (!quickSparringValue) return;

    quickSparringValue.classList.remove("status-no-data");

    const error = store.get('book.error');
    const stats = store.get('book.stats');

    if (error === 'notLoggedIn') {
        // Live statistics come from Lichess with the user's own token: logging in alone is not enough
        quickSparringValue.textContent = "Needs a Lichess account";
        quickSparringValue.classList.add("status-no-data");
        if (quickSparringBar) quickSparringBar.innerHTML = "";
        return;
    }

    if (error === 'lichessNotConnected') {
        quickSparringValue.textContent = "Connect Lichess";
        quickSparringValue.classList.add("status-no-data");
        if (quickSparringBar) quickSparringBar.innerHTML = "";
        return;
    }

    if (!stats) {
        quickSparringValue.textContent = "-";
        quickSparringValue.classList.add("status-no-data");
        if (quickSparringBar) quickSparringBar.innerHTML = "";
        return;
    }

    const userSide = config.getSelectedBoardSide()[0]; // 'w' or 'b'
    const sparringPercent = (userSide == 'w') ?
        `<span class="status-pct-win">${stats.whitePct}%</span> <span class="status-pct-draw">${stats.drawPct}%</span> <span class="status-pct-loss">${stats.blackPct}%</span>` :
        `<span class="status-pct-win">${stats.blackPct}%</span> <span class="status-pct-draw">${stats.drawPct}%</span> <span class="status-pct-loss">${stats.whitePct}%</span>`;

    quickSparringValue.innerHTML = `<div class="status-percentages">${sparringPercent}</div>`;

    if (quickSparringBar) {
        const winPct = userSide === 'w' ? stats.whitePct : stats.blackPct;
        const lossPct = userSide === 'w' ? stats.blackPct : stats.whitePct;
        quickSparringBar.innerHTML = `
            <div class="status-bar-white" style="width: ${winPct}%"></div>
            <div class="status-bar-draw" style="width: ${stats.drawPct}%"></div>
            <div class="status-bar-black" style="width: ${lossPct}%"></div>
        `;
    }
}

function renderSparringDetail() {
    if (!quickSparringDetail) return;
    const moves = store.get('book.latestMoves') || [];

    if (!moves.length) {
        quickSparringDetail.innerHTML = '<div class="detail-empty">No moves</div>';
        return;
    }

    const totalPool = moves.reduce((sum, m) => sum + (m.white || 0) + (m.black || 0) + (m.draws || 0), 0);

    const userSide = config.getSelectedBoardSide()[0]; // 'w' or 'b'
    const isWhite = userSide === 'w';

    const rows = moves.slice(0, 7).map(m => {
        const total = (m.white || 0) + (m.black || 0) + (m.draws || 0);
        const winPct = total ? (((isWhite ? m.white : m.black) / total) * 100).toFixed(0) : 0;
        const drawPct = total ? ((m.draws / total) * 100).toFixed(0) : 0;
        const lossPct = total ? (((isWhite ? m.black : m.white) / total) * 100).toFixed(0) : 0;
        const sharePct = totalPool ? ((total / totalPool) * 100).toFixed(0) : 0;
        return `<div class="detail-row">
            <span class="detail-move">${m.san}</span>
            <span class="detail-wdl"><span class="status-pct-win">${winPct}%</span><span class="status-pct-draw">${drawPct}%</span><span class="status-pct-loss">${lossPct}%</span></span>
            <span class="detail-games">${formatNumber(total)} - ${sharePct}%</span>
        </div>`;
    }).join("");

      var html = `<div class="detail-section-title">Moves they played in this position</div>
            ${rows}`;
    quickSparringDetail.innerHTML = html;
}

function renderMyGamesStats() {
    if (!quickMyGamesValue) return;

    quickMyGamesValue.classList.remove("status-pct-win", "status-pct-loss", "status-no-data");

    const error = store.get('myGames.error');
    const isLoading = store.get('myGames.isLoading');
    const stats = store.get('myGames.stats');

    if (error === 'notLoggedIn') {
        quickMyGamesValue.textContent = "Log in to view";
        quickMyGamesValue.classList.add("status-no-data");
        if (quickMyGamesBar) quickMyGamesBar.innerHTML = "";
        if (quickMyGamesTotal) quickMyGamesTotal.textContent = "";
        return;
    }

    if (isLoading) {
        quickMyGamesValue.textContent = "Loading...";
        quickMyGamesValue.classList.add("status-no-data");
        return;
    }

    if (!stats) {
        quickMyGamesValue.textContent = "No games";
        quickMyGamesValue.classList.add("status-no-data");
        if (quickMyGamesBar) quickMyGamesBar.innerHTML = "";
        if (quickMyGamesTotal) quickMyGamesTotal.textContent = "";
        return;
    }

    // The "see your N next moves" hint is off: the card's own button says it better
    const hint = "";
    quickMyGamesValue.innerHTML = `
    <div class="status-percentages"><span class="status-pct-win">${stats.winPct}%</span> <span class="status-pct-draw">${stats.drawPct}%</span> <span class="status-pct-loss">${stats.lossPct}%</span></div>${hint}`;

    if (quickMyGamesBar) {
        quickMyGamesBar.innerHTML = `
            <div class="status-bar-win" style="width: ${stats.winPct}%"></div>
            <div class="status-bar-draw" style="width: ${stats.drawPct}%"></div>
            <div class="status-bar-loss" style="width: ${stats.lossPct}%"></div>
        `;
    }

    if (quickMyGamesTotal) {
        quickMyGamesTotal.textContent = `${formatNumber(stats.total)} games`;
    }
}

function renderMyGamesDetail() {
    if (!quickMyGamesDetail) return;

    const moves = store.get('myGames.latestMoves') || [];
    const plans = store.get('myGames.plans') || [];
    const diffs = store.get('myGames.moveDiffs') || [];
    const error = store.get('myGames.error');

    if (error === 'notLoggedIn' || (!moves.length && !plans.length)) {
        quickMyGamesDetail.innerHTML = moves.length === 0 && error !== 'notLoggedIn'
            ? '<div class="detail-empty">No games found for this position</div>'
            : '';
        return;
    }

    // Build diff lookup by UCI
    const diffByUci = {};
    for (const d of diffs) {
        diffByUci[d.move] = d;
    }

    let html = "";

    // Next moves section
    if (moves.length) {
        const totalPool = moves.reduce((sum, m) => sum + (m.total || 0), 0);
        const moveRows = moves.slice(0, 5).map(m => {
            const winPct = m.total ? ((m.wins / m.total) * 100).toFixed(0) : 0;
            const drawPct = m.total ? ((m.draws / m.total) * 100).toFixed(0) : 0;
            const lossPct = m.total ? ((m.losses / m.total) * 100).toFixed(0) : 0;
            const sharePct = totalPool ? ((m.total / totalPool) * 100).toFixed(0) : 0;
            const san = m.move ? uciToSan(m.move, getMyGamesFen()) : '?';

            // Diff badge
            const d = m.move ? diffByUci[m.move] : null;
            let diffHtml = '';
            if (d && d.diff !== null) {
                const cls = d.diff > 0 ? 'detail-diff--positive' : d.diff < 0 ? 'detail-diff--negative' : 'detail-diff--neutral';
                const sign = d.diff > 0 ? '+' : '';
                diffHtml = `<span class="detail-diff ${cls}" title="You: ${d.myWinPct}% vs DB: ${d.sparringWinPct}%">${sign}${d.diff}%</span>`;
            }

            return `<div class="detail-row">
                <span class="detail-move">${san}${diffHtml}</span>
                <span class="detail-wdl"><span class="status-pct-win">${winPct}%</span><span class="status-pct-draw">${drawPct}%</span><span class="status-pct-loss">${lossPct}%</span></span>
                <span class="detail-games">${formatNumber(m.total)} - ${sharePct}%</span>
            </div>`;
        }).join("");

        html += `
            <div class="detail-section-title">Moves you played in this position</div>
            ${moveRows}`;
    }

    // Plans section (only sequences of 2+ moves)
    const multiMovePlans = Array.isArray(plans) ? plans.filter(p => p.moves && p.moves.length >= 2) : [];
    if (multiMovePlans.length) {
        const planRows = multiMovePlans.slice(0, 5).map(p => {
            const winPct = p.total ? ((p.wins / p.total) * 100).toFixed(0) : 0;
            const sanMoves = uciSequenceToSan(p.moves, getMyGamesFen());
            const movesStr = sanMoves.join(" ");
            return `<div class="detail-row detail-row--plan">
                <span class="detail-plan-moves">${movesStr}</span>
                <span class="detail-plan-stats">${p.total}g <span class="status-pct-win">+${winPct}%</span></span>
            </div>`;
        }).join("");

        // disable plans until we have better data
        //html += `<div class="detail-section-title">Plans</div>${planRows}`;
    }

    quickMyGamesDetail.innerHTML = html;
}

// === HEADER RENDERERS ===

function renderMyGamesHeader() {
    const headerEl = document.querySelector("#quickMyGamesStatus .card__title");
    if (!headerEl) return;
    const side = store.get('board.userSide') || 'white';
    const sideLabel = side === 'white' ? 'as white' : 'as black';
    headerEl.innerHTML = `My Games <span class="card__title-note">${sideLabel}</span>`;
}

// === FOOTER RENDERERS ===

function toggleCardExpansion(cardId, footerRenderer) {
    const card = document.getElementById(cardId);
    if (!card) return;
    card.classList.toggle('expanded');
    footerRenderer?.();
}

function detailsButtonHtml(cardId, labelShow, labelHide) {
    const card = document.getElementById(cardId);
    const label = card?.classList.contains('expanded') ? labelHide : labelShow;
    return `<button type="button" class="card__link" data-action="details" title="${label}">${label}</button>`;
}

function renderMyGamesFooter() {
    if (!quickMyGamesFooter) return;
    const myStats = store.get('myGames.stats') || null;
    const sparringStats = store.get('book.stats') || null;
    if (!myStats || !sparringStats) {
        quickMyGamesFooter.innerHTML = `<div class="repertoire-footer-row">
            <span class="repertoire-footer-text"></span>
            ${detailsButtonHtml('quickMyGamesStatus', 'Show my moves', 'Hide my moves')}
        </div>`;
        wireCardDetailsButton(quickMyGamesFooter, 'quickMyGamesStatus', renderMyGamesFooter);
        return;
    }

    const userSide = config.getSelectedBoardSide()[0]; // 'w' or 'b'
    const diff = userSide == 'w' ?
      myStats.winPct - sparringStats.whitePct :
      myStats.winPct - sparringStats.blackPct;

    let text;
    if (diff > 1) {
        text = `<span class="status-pct-win">+${diff}%</span> you're above the average`;
    } else if (diff < -1) {
        text = `<span class="status-pct-loss">${diff}%</span> you can do better`;
    } else {
        text = `On par with sparring`;
    }

    quickMyGamesFooter.innerHTML = `<div class="repertoire-footer-row">
        <span class="repertoire-footer-text">${text}</span>
        ${detailsButtonHtml('quickMyGamesStatus', 'Show my moves', 'Hide my moves')}
    </div>`;
    wireCardDetailsButton(quickMyGamesFooter, 'quickMyGamesStatus', renderMyGamesFooter);
}

function renderSparringFooter() {
    if (!quickSparringFooter) return;
    const stats = store.get('book.stats');
    const statsText = (stats && stats.total) ? `${formatNumber(stats.total)} games` : '';

    quickSparringFooter.innerHTML = `<div class="repertoire-footer-row">
        <span class="repertoire-footer-text">${statsText}</span>
        ${detailsButtonHtml('quickSparringStatus', 'Show sparring moves', 'Hide sparring moves')}
    </div>`;
    wireCardDetailsButton(quickSparringFooter, 'quickSparringStatus', renderSparringFooter);
}

function wireCardDetailsButton(footerEl, cardId, rerender) {
    const btn = footerEl.querySelector('[data-action="details"]');
    if (!btn) return;
    btn.addEventListener('click', (e) => {
        e.stopPropagation();
        toggleCardExpansion(cardId, rerender);
    });
}
